import React, { useState } from 'react';
import { activityLogService } from '../../services/activityLogService';
import './QuickExercises.css';

const QuickExercises = ({ topic, customExercises, userId, courseId }) => {
  const [currentExercise, setCurrentExercise] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Use custom exercises if available, otherwise use defaults
  const defaultExercises = [
    {
      type: 'visual',
      question: 'Which one is a variable?',
      options: [
        { text: 'x = 5', emoji: '📦', correct: true },
        { text: 'print()', emoji: '🖨️', correct: false },
        { text: 'if/else', emoji: '❓', correct: false },
        { text: 'for loop', emoji: '🔄', correct: false }
      ]
    },
    {
      type: 'fill-blank',
      question: 'Complete: print("___")',
      options: ['Hello', 'World', 'Python', 'Code'],
      correct: 'Hello',
      hint: 'What do we usually print first?'
    },
    {
      type: 'true-false',
      question: 'Python is easy to learn!',
      correct: true
    }
  ];

  const exercises = customExercises && customExercises.length > 0 ? customExercises : defaultExercises;

  const handleAnswer = (answer, correct) => {
    setAnswers({ ...answers, [currentExercise]: answer });
  };

  const nextExercise = async () => {
    if (currentExercise < exercises.length - 1) {
      // Log exercise completion
      if (userId && courseId) {
        await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.EXERCISE_COMPLETE, {
          courseId,
          topicName: topic?.name,
          exerciseIndex: currentExercise,
          exerciseType: currentEx.type
        });
      }
      setCurrentExercise(currentExercise + 1);
    } else {
      // Log final exercise completion
      if (userId && courseId) {
        await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.EXERCISE_COMPLETE, {
          courseId,
          topicName: topic?.name,
          exerciseIndex: currentExercise,
          exerciseType: currentEx.type,
          completed: true
        });
      }
      setShowResults(true);
    }
  };

  const currentEx = exercises[currentExercise];

  return (
    <div className="quick-exercises">
      <div className="exercise-header">
        <h4>⚡ Quick Exercise: {topic?.name || 'Practice'}</h4>
        <div className="exercise-progress">
          {currentExercise + 1} / {exercises.length}
        </div>
      </div>

      {!showResults ? (
        <div className="exercise-content">
          <div className="exercise-question">
            <p>{currentEx.question}</p>
          </div>

          {currentEx.type === 'visual' && (
            <div className="visual-options">
              {currentEx.options.map((option, idx) => (
                <button
                  key={idx}
                  className={`visual-option ${answers[currentExercise] === idx ? (option.correct ? 'correct' : 'wrong') : ''}`}
                  onClick={() => handleAnswer(idx, option.correct)}
                >
                  <span className="option-emoji">{option.emoji}</span>
                  <span className="option-text">{option.text}</span>
                </button>
              ))}
            </div>
          )}

          {currentEx.type === 'fill-blank' && (
            <div className="fill-blank-exercise">
              <div className="code-example">
                print("<span className="blank">{answers[currentExercise] || '___'}</span>")
              </div>
              <div className="blank-options">
                {currentEx.options.map((option, idx) => (
                  <button
                    key={idx}
                    className={`blank-option ${answers[currentExercise] === option ? 'selected' : ''}`}
                    onClick={() => handleAnswer(option, option === currentEx.correct)}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {currentEx.hint && (
                <p className="exercise-hint">💡 Hint: {currentEx.hint}</p>
              )}
            </div>
          )}

          {currentEx.type === 'true-false' && (
            <div className="true-false-exercise">
              <button
                className={`tf-btn ${answers[currentExercise] === true ? 'selected' : ''}`}
                onClick={() => handleAnswer(true, currentEx.correct === true)}
              >
                ✅ True
              </button>
              <button
                className={`tf-btn ${answers[currentExercise] === false ? 'selected' : ''}`}
                onClick={() => handleAnswer(false, currentEx.correct === false)}
              >
                ❌ False
              </button>
            </div>
          )}

          {answers[currentExercise] !== undefined && (
            <div className="exercise-feedback">
              {currentEx.type === 'visual' && currentEx.options[answers[currentExercise]]?.correct ? (
                <div className="feedback correct-feedback">
                  🎉 Awesome! You got it right!
                </div>
              ) : currentEx.type === 'fill-blank' && answers[currentExercise] === currentEx.correct ? (
                <div className="feedback correct-feedback">
                  🎉 Perfect! That's correct!
                </div>
              ) : currentEx.type === 'true-false' && answers[currentExercise] === currentEx.correct ? (
                <div className="feedback correct-feedback">
                  🎉 Great job! You're right!
                </div>
              ) : (
                <div className="feedback wrong-feedback">
                  💭 Not quite! Try again!
                </div>
              )}
            </div>
          )}

          <div className="exercise-actions">
            <button
              onClick={nextExercise}
              disabled={answers[currentExercise] === undefined}
              className="next-exercise-btn"
            >
              {currentExercise < exercises.length - 1 ? 'Next Exercise →' : 'See Results 🎯'}
            </button>
          </div>
        </div>
      ) : (
        <div className="exercise-results">
          <h3>🎊 Exercise Complete!</h3>
          <div className="results-summary">
            <p>You completed {exercises.length} exercises!</p>
            <div className="celebration">
              🎉 🎉 🎉
            </div>
          </div>
          <button
            onClick={() => {
              setCurrentExercise(0);
              setAnswers({});
              setShowResults(false);
            }}
            className="restart-btn"
          >
            🔄 Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickExercises;

